import {
  Calendar,
  CheckCheck,
  Coins,
  Text,
  Wrench,
} from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import type { FunctionComponent } from "react";
import type { GetThemeValueForKey } from "tamagui";
import { ModuleDoneType, ModuleType } from "../../templates/types";
import { currenciesArray } from "../modules/ExpenseModule/currencies";
import type { IconProp } from "../ui/icons";
import { type FormSheetItems, FormSheetType } from "./components/FormDataModal";

export type IconType =
  | JSX.Element
  | FunctionComponent<{ color?: GetThemeValueForKey<"color">; size?: number }>
  | null;

export type ModuleMetaCategory = {
  title?: string;
  icon?: IconProp;
  modules: ModuleMetaData[];
};

export type ModuleMetaData = {
  type: ModuleType;
  doneType: ModuleDoneType;
  title: string;
  icon: string;
  description: string;
  helpText?: string;
  examples?: string;
  extraData?: FormSheetItems;
  // biome-ignore lint/suspicious/noExplicitAny: Because
  formValues?: { [key: string]: any };
  onlyInDev?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: Because
  prepareData?: (inData: any) => any;
};

export const allModulesAvailable: ModuleMetaCategory[] = [
  {
    //title: "Decide on",
    //icon: MessageCircleQuestion,
    modules: [
      {
        icon: "📅",
        doneType: ModuleDoneType.Voting,
        type: ModuleType.Calendar,
        title: "Dates",
        description: "Select dates when you're available",
        examples:
          "Find a date when everyone is available for a party, weekend trip or anything else",
        helpText:
          "With the Calendar, you can efficiently plan and organize events by selecting dates when you and others are available. Whether you're scheduling a party, organizing a weekend trip, or coordinating team meetings, the Calendar module provides an intuitive interface for viewing and selecting dates. Seamlessly find the perfect date that works for everyone, ensuring smooth coordination and hassle-free planning. Utilize the Calendar module's features to streamline your scheduling process and keep track of important dates and events.",
      },
      {
        icon: "🌍",
        type: ModuleType.Location,
        doneType: ModuleDoneType.Voting,
        title: "Locations",
        description: "Add locations and vote for which one is the best!",
        examples: "Add locations and rate them with stars (1-5)",
        helpText:
          "Explore and decide on the best places with the Location card. Add various locations and allow participants to vote for their favorites. Whether you're planning a team outing, selecting a venue for an event, or simply deciding on a place to hang out, the Location card provides a convenient way to gather opinions and make informed decisions. Participants can rate locations using stars (1-5), helping you to identify the most popular choices. Utilize the Location card's interactive features to streamline the decision-making process and ensure everyone's preferences are considered.",
      },
      {
        icon: "📥",
        type: ModuleType.Poll,
        doneType: ModuleDoneType.Voting,
        title: "Poll / Vote",
        description: "Add a question and wait for answers",
        examples:
          "Valid passports? What movie do you prefer? Budget? What to eat?",
        extraData: [
          {
            label: "Multiple choices allowed",
            type: FormSheetType.Checkbox,
            formKey: "settings.multipleVotes",
            icon: CheckCheck,
          },
          {
            label: "Show description",
            type: FormSheetType.Checkbox,
            formKey: "settings.showDescription",
            icon: Text,
          },
        ],
        formValues: {
          "settings.multipleVotes": true,
        },
        helpText:
          "Engage your audience and gather opinions with the Poll card. Simply add a question and await responses from participants. Whether you're collecting preferences for movie choices, gathering feedback on budget priorities, or seeking consensus on travel plans, the Poll card offers a versatile platform for soliciting input. Additionally, customize your polls with options such as allowing multiple choices and displaying descriptions, ensuring flexibility and clarity in your polling process. Encourage interaction and facilitate decision-making with the Poll card's intuitive interface and customizable features.",
      },
      {
        icon: "🕥",
        type: ModuleType.Reminder,
        doneType: ModuleDoneType.Voting,
        title: "Reminder",
        description:
          "Based on dates, recieve reminders, add information and set owners.",
        examples: "Set a date, information and get reminders",
        helpText:
          "Stay organized and on track with the Reminder card. Based on dates, receive timely reminders, add relevant information, and assign owners. Whether it's deadlines for projects, appointments, or personal events, the Reminder card ensures that important dates are never missed. Simply set a date, provide necessary information, and receive reminders to stay proactive and efficient. With customizable features and intuitive functionality, the Reminder card helps you manage your schedule effectively and stay ahead of your tasks.",
        extraData: [
          {
            label: "Due date",
            placeholder: "Due date",
            type: FormSheetType.Date,
            formKey: "dateDue",
            icon: Calendar,
          },
        ],
        prepareData: (data) => {
          data.dateDue = Timestamp.fromDate(new Date(data.dateDue));

          return data;
        },
      },
      {
        icon: "✅",
        type: ModuleType.Checkboxes,
        doneType: ModuleDoneType.None,
        title: "Todo list",
        description: "Make them checked as you go",
        examples:
          "Shopping lists, assignments, backpacks and whatever else you can think of.",
        helpText:
          "Are you juggling multiple tasks and need a central place to keep track of them? The Todo's module is here to help! Whether it's shopping lists, assignments, or simply things you don't want to forget, this module provides a convenient way to organize your tasks. You can easily add, edit, and check off tasks in the Todo's module. Stay organized and on top of your to-do list with this handy tool!",
      },
      {
        icon: "👍",
        doneType: ModuleDoneType.Voting,
        type: ModuleType.Rsvp,
        title: "Attendees",
        description: "Find attendees",
        examples: "Let everyone say if they are attending or not",
        helpText:
          "Find out who's in and let your group easily share their responses.",
      },
    ],
  },

  {
    title: "Tools",
    icon: Wrench,
    modules: [
      {
        icon: "💰",
        type: ModuleType.Expense,
        doneType: ModuleDoneType.None,
        title: "Expenses",
        description: "Add expenses and split bills easily",
        examples: "Split bills and keep track of your spending spree",
        helpText:
          "Keep track of your spending and split bills effortlessly with the Expenses card. Add expenses incurred and easily divide bills among participants. Whether you're sharing costs with friends, tracking business expenses, or managing personal finances, the Expenses card simplifies the process of recording and managing expenditures. Split bills seamlessly and stay on top of your spending spree, ensuring financial transparency and accountability.",
        extraData: [
          {
            label: "Currency",
            placeholder: "Currency",
            type: FormSheetType.Select,
            options: currenciesArray.map((currency) => ({
              label: `${currency.name} (${currency.code})`,
              value: currency.code,
            })),
            formKey: "currencyCode",
            icon: Coins,
          },
        ],
        formValues: { currencyCode: "USD" },
      },
      {
        icon: "🏆",
        type: ModuleType.Tracker,
        doneType: ModuleDoneType.None,
        title: "Score Tracker",
        description: "Keep track on score entries",
        examples: "Keep score of something - steps, poker-wins, hiccups?",
        helpText: "",
      },

      {
        icon: "👀",
        type: ModuleType.Info,
        doneType: ModuleDoneType.None,
        title: "Information",
        description: "Add any information and discuss about it if needed",
        examples: "Add an information area with text",
        helpText:
          "Add any infomation you want here in this area, you will get access to our editor in which you can add links and format your information as needed.",
      },
    ],
  },
];
